import { gql } from '@apollo/client';
import { User } from '../types/user/user';
import { forPaginated } from './common/paginated.graphql';
import { CompanyRoleAssignmentFragment } from './companyRoleAssignment.graphql';
import { CompanyRoleAssignment } from '../types/company_role_assignment/companyRoleAssignment';
import { CreateCompanyRoleAssignmentInput } from '../types/company_role_assignment/companyRoleAssignment.input';

// export enum AuthenticationResultType {
//   normal = 'normal',
//   authenticationCodeRequired = 'authenticationCodeRequired',
// }

const user = gql`
  fragment User on User {
    id
    version
    firstName
    lastName
    email
    userId
    createdAt
    createdBy
    updatedAt
    updatedBy
    acceptedAgreements
    acceptedPrivacyPolicy
    acceptedTerms
    isOnboarded
    language
    locale
    notificationsToken
    unitSetting
    profilePicture
    supportHMAC
    verified
  }
`;

const signInToken = gql`
  fragment SignInToken on AuthenticationToken {
    accessToken
    refreshToken
    tokenType
    expiresIn
    scope
  }
`;

const get = gql`
  query User {
    user {
      ...User
    }
  }
  ${user}
`;

export interface GetUserResponse {
  user: User;
}

const getForCompany = gql`
  query Users($companyId: String!, $page: Float) {
    users(companyId: $companyId, page: $page) {
      data {
        userId
        ...User
      }
      page
      pageSize
      batchSize
      hasNext
    }
  }
  ${user}
`;

export interface GetUsersVariables {
  companyId: string;
  page?: number;
}

export interface GetUsersResponse {
  users: PaginatedUser;
}

export class PaginatedUser extends forPaginated<User>() {}

const invite = gql`
  mutation InviteUsers($role: CreateCompanyRoleAssignmentInput!, $emails: [String!]!) {
    inviteUsers(role: $role, emails: $emails) {
      ...CompanyRoleAssignment
    }
  }
  ${CompanyRoleAssignmentFragment.companyRoleAssignment}
`;

export interface InviteUsersVariables {
  companyId: string;
  emails: string[];
  role: CreateCompanyRoleAssignmentInput;
}

export interface InviteUsersResponse {
  inviteUsers: CompanyRoleAssignment[];
}

const verify = gql`
  mutation VerifyEmail($validationCode: String!, $email: String!) {
    verifyEmail(validationCode: $validationCode, email: $email)
  }
`;

export interface VerifyEmailVariables {
  validationCode: string;
  email: string;
}

export interface VerifyEmailResponse {
  verifyEmail: boolean;
}

const acceptLicenseAgreement = gql`
  mutation AcceptLicenseAgreement {
    acceptLicenseAgreement {
      ...User
    }
  }
  ${user}
`;

export interface AcceptLicenseAgreementVariables {}

export interface AcceptLicenseAgreementResponse {
  acceptLicenseAgreement: User;
}

const acceptPrivacyPolicy = gql`
  mutation AcceptPrivacyPolicy {
    acceptPrivacyPolicy {
      ...User
    }
  }
  ${user}
`;

export interface AcceptPrivacyPolicyVariables {}

export interface AcceptPrivacyPolicyResponse {
  acceptPrivacyPolicy: User;
}

const acceptTermsOfUse = gql`
  mutation AcceptTermsOfUse {
    acceptTermsOfUse {
      ...User
    }
  }
  ${user}
`;

export interface AcceptTermsOfUseVariables {}

export interface AcceptTermsOfUseResponse {
  acceptTermsOfUse: User;
}

const update = gql`
  mutation UpdateUser($user: UserInput!) {
    updateUser(user: $user) {
      ...User
    }
  }
  ${user}
`;

export interface UpdateUserVariables {
  user: User;
}

export interface UpdateUserResponse {
  updateUser: User;
}

const changePassword = gql`
  mutation ChangePassword($newPassword: String!, $oldPassword: String!) {
    changePassword(newPassword: $newPassword, oldPassword: $oldPassword) {
      ...User
    }
  }
  ${user}
`;

export interface ChangePasswordVariables {
  newPassword: string;
  oldPassword: string;
}

export interface ChangePasswordResponse {
  changePassword: User;
}

const resetPassword = gql`
  query ResetPasswordUrl($redirectUrl: String!) {
    resetPasswordUrl(redirectUrl: $redirectUrl)
  }
`;

export interface ResetPasswordVariables {
  redirectUrl: string;
}

export interface ResetPasswordResponse {
  resetPasswordUrl: string;
}

const forgotPassword = gql`
  query ResetPassword($email: String!) {
    resetPassword(email: $email)
  }
`;

export interface ForgotPasswordVariables {
  email: string;
}

export interface ForgotPasswordResponse {
  resetPassword: boolean;
}

const resetUserPassword = gql`
  mutation ResetUserPassword($id: String!) {
    resetUserPassword(id: $id)
  }
`;

export interface ResetUserPasswordVariables {
  id: string;
}

export interface ResetUserPasswordResponse {
  resetUserPassword: boolean;
}

const verifyUserEmail = gql`
  mutation VerifyUserEmail($id: String!) {
    verifyUserEmail(id: $id)
  }
`;

export interface VerifyUserEmailVariables {
  id: string;
}

export interface VerifyUserEmailResponse {
  verifyUserEmail: boolean;
}

const activateMicrosoftSubscription = gql`
  mutation ActivateMicrosoftSubscription($token: String!) {
    activateMicrosoftSubscription(token: $token)
  }
`;

export interface ActivateMicrosoftSubscriptionVariables {
  token: string;
}

export interface ActivateMicrosoftSubscriptionResponse {
  activateMicrosoftSubscription: string;
}

export const UserQueries = {
  get,
  getForCompany,
  verify,
  resetPassword,
  forgotPassword,
};

export const UserMutations = {
  invite,
  changePassword,
  acceptLicenseAgreement,
  acceptPrivacyPolicy,
  acceptTermsOfUse,
  update,
  resetUserPassword,
  verifyUserEmail,
  activateMicrosoftSubscription,
};

export const UserFragment = {
  user,
};
